import { z } from 'zod'
import { zId, zIsoDate } from '../common'

// GET /admin/locale_audit?tenantId=… — multilingual quality report for one
// tenant. Verified against a live payload. Severity and per-language `status`
// are kept as loose strings (mapped to tones app-side with a neutral fallback)
// so a new backend value never rejects the parse.

export const zLocaleAuditFinding = z.object({
  id: z.string(),
  severity: z.string(),
  groupId: z.string(),
  language: z.string(),
  slug: z.string(),
  message: z.string(),
})
export type LocaleAuditFinding = z.infer<typeof zLocaleAuditFinding>

export const zLocaleAuditLanguage = z.object({
  language: z.string(),
  status: z.string(),
  slug: z.string(),
  title: z.string(),
  published: z.boolean(),
  stale: z.boolean(),
})
export type LocaleAuditLanguage = z.infer<typeof zLocaleAuditLanguage>

export const zLocaleAuditGroup = z.object({
  groupId: z.string(),
  original: z.object({ id: zId, slug: z.string(), title: z.string() }),
  // { [language]: statusString } — one coverage status per expected language.
  coverage: z.record(z.string()),
  languages: z.array(zLocaleAuditLanguage),
})
export type LocaleAuditGroup = z.infer<typeof zLocaleAuditGroup>

export const zLocaleAuditSummary = z.object({
  groups: z.number(),
  expected: z.number(),
  ok: z.number(),
  stale: z.number(),
  missing: z.number(),
  unpublished: z.number(),
  coveragePct: z.number(),
  // Counts keyed by severity; catchall keeps unseen severities from rejecting.
  findingsBySeverity: z.record(z.number()).default({}),
})
export type LocaleAuditSummary = z.infer<typeof zLocaleAuditSummary>

export const zLocaleAudit = z.object({
  tenantId: zId,
  generatedAt: zIsoDate,
  defaultLanguage: z.string(),
  xDefaultLanguage: z.string(),
  expectedLanguages: z.array(z.string()),
  languagePackCurated: z.record(z.boolean()).default({}),
  summary: zLocaleAuditSummary,
  groups: z.array(zLocaleAuditGroup),
  findings: z.array(zLocaleAuditFinding),
})
export type LocaleAudit = z.infer<typeof zLocaleAudit>
